import { InvestmentAccount } from '@/hooks/useInvestments';
import { InvestmentType } from '@/hooks/useInvestmentTypes';
import { useInvestmentCurrencyConversion } from '@/hooks/useInvestmentCurrencyConversion';
import type { InvestmentAccountWithConvertedBalance } from '@/hooks/useInvestmentCurrencyConversion';
import { PortfolioSummaryCard } from '@/components/investment-summary/PortfolioSummaryCard';
import { InvestmentDistributionChart } from '@/components/investment-charts/InvestmentDistributionChart';
import { InvestmentListByType } from '@/components/investment-list/InvestmentListByType';
import { Button } from '@/components/ui/button';
import { Plus, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMemo } from 'react';

interface InvestmentOverviewProps {
  investments: InvestmentAccount[];
  types: InvestmentType[];
  onAddInvestment: () => void;
  onEditInvestment: (investment: InvestmentAccount) => void;
  onDeleteInvestment: (id: string) => void;
  isLoading?: boolean;
}

/**
 * InvestmentOverview component displays the overview tab with aggregated portfolio metrics,
 * distribution chart, and investments grouped by type.
 *
 * @param investments - Array of all investment accounts
 * @param types - Array of investment types
 * @param onAddInvestment - Callback when add investment button is clicked
 * @param onEditInvestment - Callback when edit investment is triggered
 * @param onDeleteInvestment - Callback when delete investment is triggered
 * @param isLoading - Loading state indicator
 */
export function InvestmentOverview({
  investments,
  types,
  onAddInvestment,
  onEditInvestment,
  onDeleteInvestment,
  isLoading = false,
}: InvestmentOverviewProps) {
  const baseCurrency = useMemo(() => investments[0]?.currency ?? 'USD', [investments]);

  const {
    selectedCurrency,
    setSelectedCurrency,
    convertedInvestments,
    totalNetWorth,
    isConverting,
    conversionError,
  } = useInvestmentCurrencyConversion({ baseCurrency, investments });

  // Group converted investments by type
  const investmentsByType = useMemo(() => {
    return convertedInvestments.reduce((acc, investment) => {
      const typeId = investment.investment_type_id;
      if (!acc[typeId]) acc[typeId] = [];
      acc[typeId].push(investment);
      return acc;
    }, {} as Record<string, InvestmentAccountWithConvertedBalance[]>);
  }, [convertedInvestments]);

  const handleCurrencyToggle = () => {
    // Cycle through common currencies
    const currencies = ['USD', 'EUR', 'GBP', 'KES'];
    const currentIndex = currencies.indexOf(selectedCurrency);
    const nextIndex = (currentIndex + 1) % currencies.length;
    setSelectedCurrency(currencies[nextIndex]);
  };

  // Empty state
  if (!isLoading && investments.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="py-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-muted p-4" aria-hidden="true">
                <Plus className="h-8 w-8 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">No investments yet</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Start tracking your investment portfolio by adding your first investment.
                </p>
              </div>
              <Button onClick={onAddInvestment} className="mt-2" aria-label="Add your first investment">
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Currency conversion alerts */}
      {isConverting && (
        <Alert role="status" aria-live="polite" aria-busy="true">
          <AlertDescription>Converting to {selectedCurrency}…</AlertDescription>
        </Alert>
      )}
      {conversionError && (
        <Alert variant="destructive" role="alert" aria-live="assertive">
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            Failed to convert currencies. Displaying amounts in original currencies.
          </AlertDescription>
        </Alert>
      )}

      {/* Portfolio Summary Card */}
      <PortfolioSummaryCard
        title='Investments'
        totalValue={totalNetWorth}
        currency={selectedCurrency}
        investmentsByType={investmentsByType}
        isLoading={isLoading}
        onCurrencyToggle={handleCurrencyToggle}
      />

      {/* Distribution Chart */}
      <InvestmentDistributionChart
        investments={convertedInvestments}
        currency={selectedCurrency}
        isLoading={isLoading}
      />

      {/* Investments List Grouped by Type */}
      <InvestmentListByType
        investmentsByType={investmentsByType}
        currency={selectedCurrency}
        onEdit={onEditInvestment}
        onDelete={onDeleteInvestment}
        isLoading={isLoading}
      />
    </div>
  );
}
